import React, { useState, useEffect, useLayoutEffect, useRef, ChangeEvent, useCallback, lazy, Suspense } from 'react';
import { checkCharacterConsistency, updateScriptWithSuggestion, injectPlotFix, generatePlotFixOptions } from '@/services/aiService';
import { saveToLocalStorageChunked, loadFromLocalStorageChunked, removeFromLocalStorageChunked } from '@/utils/storageUtils';
import { Toaster, toast } from 'sonner';
import { ChevronLeft, ChevronRight, PenTool, User, FileText, X, Loader2, Link, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { TabOption, AppState, AppConfig, ScriptBlock, Project, ChatMessage, ScriptVersion, BlockType, StageMode, SceneCameraDirection, SceneEffectsResult, AnalysisResult, DurationEstimate, SceneShotList, ShotItem, PromptSet, AnalysisData, PillarDefinition, CharacterProfile } from '@/types';
import { METHOD_MCKEE_RULES, METHOD_FIELD_RULES, METHOD_HYBRID_RULES } from '@/features/knowledge/SystemKnowledge';
import { useScriptAnalysis } from '@/hooks/useScriptAnalysis';
import { TRANSLATIONS, Language } from '@/translations';
import { splitMetadataAndParse, createTitlePageBlocks, translateVersionName, serializeBlocksToFountain, parseTextToBlocks } from '@/utils/scriptUtils';
import { generateUUID } from '@/utils/uuid';
import { getPdfParserWorker } from '@/workers/pdfParserInstance';
import { getAuteurFormats, AuteurFormat, DEFAULT_FORMATS } from '@/services/formatService';

// Lazy load heavy components
import Auth from '@/components/auth/Auth';
const SidebarLeft = lazy(() => import('@/components/layout/SidebarLeft').then(module => ({ default: module.SidebarLeft })));
const MainStage = lazy(() => import('@/components/layout/MainStage').then(module => ({ default: module.MainStage })));
const SidebarRight = lazy(() => import('@/components/layout/SidebarRight').then(module => ({ default: module.SidebarRight })));

const LoadingFallback = () => (
  <div className="flex-1 flex items-center justify-center bg-background h-full">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      <span className="text-sm text-text-secondary animate-pulse">Initializing AuteurDraft...</span>
    </div>
  </div>
);

// Lazy load pages
const PrivacyPolicy = lazy(() => import('@/components/layout/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const LandingPage = lazy(() => import('@/components/layout/LandingPage').then(module => ({ default: module.LandingPage })));
const DocsPage = lazy(() => import('@/components/layout/DocsPage'));

import {
  syncAppStateToFirestore,
  addCustomMethodToFirestore,
  updateCustomMethodInFirestore,
  deleteCustomMethodFromFirestore,
  saveLearnedElement,
  loadLearnedElements,
  loadProjectsFromFirestore,
  loadUserConfig,
  deleteProjectFromFirestore
} from '@/services/firestoreService';

const getStorageKey = (uid: string) => `auteur_os_projects_v3_${uid}`; // Increment version to avoid legacy shared data
const MAX_PROJECTS_TRIAL = 6;
const MAX_HISTORY_STACK = 100;

/**
 * Prunes the app state to fit in localStorage.
 * Keeps the full structure but limits the volume of heavy items like version history and audit results.
 */
const pruneStateForStorage = (state: any) => {
  if (!state.projects) return state;

  try {
    const activeProjectId = state.activeProjectId;

    // We create a copy to avoid mutating the active memory state
    const prunedProjects = state.projects.map((project: any) => {
      const isProjectActive = project.id === activeProjectId;
      const activeId = project.activeVersionId;
      const allVersions = project.scriptVersions || [];

      // For non-active projects, we can be extremely aggressive (keep only metadata)
      if (!isProjectActive) {
        return {
          ...project,
          scriptVersions: allVersions.slice(-1).map((v: any) => ({
            ...v,
            blocks: [], // No blocks for non-active projects
            auditResult: undefined
          })),
          chatHistory: [],
          characterProfiles: {},
          verifiedSegments: []
        };
      }

      // For the active project, keep only the active version's blocks
      const prunedVersions = allVersions.filter((v: any, index: number) => {
        const isRecent = index >= allVersions.length - 2;
        const isActive = v.id === activeId;
        return isActive || isRecent;
      }).map((v: any) => {
        const isVersionActive = v.id === activeId;

        if (!isVersionActive) {
          return {
            ...v,
            blocks: [], // DROP BLOCKS for inactive versions
            auditResult: undefined,
            infoMetadata: v.infoMetadata?.substring(0, 500)
          };
        }
        return v;
      });

      return {
        ...project,
        scriptVersions: prunedVersions,
        chatHistory: (project.chatHistory || []).slice(-10),
        verifiedSegments: []
      };
    });

    return {
      ...state,
      projects: prunedProjects
    };
  } catch (e) {
    console.error("[Storage] Failed to prune state, saving minimal version.", e);
    return {
      activeProjectId: state.activeProjectId,
      config: state.config,
      projects: []
    };
  }
};

// Command Interface as requested
interface Command {
  id: string;
  type: string;
  timestamp: number;
  apply: () => void;
  revert: () => void;
  payload: any;
}

const STABLE_EMPTY_BLOCKS: ScriptBlock[] = [];

function AuteurDraft() {
  const { user, loading: authLoading, signOut } = useAuth();

  const [appState, setAppState] = useState<AppState>(() => {
    const defaultState: AppState = {
      projects: [],
      activeProjectId: 'start-menu',
      activeTab: TabOption.ACTIONS,
      activeAIModal: null,
      activeAnalysisTask: null,
      analysis: { scenes: [], characters: [], locations: [], props: [], sfx: [], cameras: [], transitions: [], totalPages: 0, totalWords: 0, estimatedPdfPages: 0 },
      config: {
        structuralAnalysis: false,
        visualAtmosphere: false,
        characterArcs: false,
        plantAndPayoff: false,
        methodMcKee: false,
        methodField: false,
        panWithSpace: true,
        linePagination: false,
        language: 'pt',
        customMethods: [],
        creativityTemperature: 0.2,
        matureLanguage: false,
        enableHorrorOps: false,
        scriptFormat: 'Film'
      },
      activeFormatId: undefined,
      activeBriefingAnswers: {}
    };

    if (typeof window !== 'undefined' && user) {
      const parsed = loadFromLocalStorageChunked(getStorageKey(user.uid));
      if (parsed) {
        return {
          ...defaultState,
          ...parsed,
          activeTab: TabOption.ACTIONS,
          activeAIModal: null,
          activeAnalysisTask: null
        };
      }
    }
    return defaultState;
  });

  const [learnedElements, setLearnedElements] = useState<Record<string, string>>({});
  const [availableFormats, setAvailableFormats] = useState<AuteurFormat[]>(DEFAULT_FORMATS);

  useEffect(() => {
    getAuteurFormats().then(setAvailableFormats);
  }, []);

  const [dataLoaded, setDataLoaded] = useState(false);

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);




  const [projectStartMode, setProjectStartMode] = useState<{ mode: StageMode, isPaste?: boolean } | null>(null);
  const [isGeneratingVersion, setIsGeneratingVersion] = useState(false);

  // New Project Creation State
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [pendingStartMode, setPendingStartMode] = useState<{ mode: StageMode, isPaste?: boolean } | null>(null);
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [newProjectAuthor, setNewProjectAuthor] = useState("");
  const [newProjectBasedOn, setNewProjectBasedOn] = useState("");
  const [newProjectContactInfo, setNewProjectContactInfo] = useState("");
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [isPlotInjecting, setIsPlotInjecting] = useState(false);
  const [isEditingTitlePage, setIsEditingTitlePage] = useState(false);

  const [navBlockId, setNavBlockId] = useState<string | null>(null);
  const [characterEditRequest, setCharacterEditRequest] = useState<{ name: string, timestamp: number } | null>(null);

  // --- HISTORY MANAGER STATE ---
  const [history, setHistory] = useState<{ past: Command[], future: Command[] }>({ past: [], future: [] });

  // Ref to track the current active version ID to clear history on switch
  const activeVersionIdRef = useRef<string | null>(null);

  // If activeProjectId is 'start-menu', this will be undefined, which is handled in MainStage
  const activeProject = appState.projects.find(p => p.id === appState.activeProjectId);
  const activeVersion = activeProject?.scriptVersions?.find(v => v.id === activeProject.activeVersionId);
  const scriptBlocks = activeVersion ? activeVersion.blocks : STABLE_EMPTY_BLOCKS;
  const hasScript = activeProject ? (activeProject.scriptVersions && activeProject.scriptVersions.length > 0) : false;

  const t = TRANSLATIONS[appState.config.language];

  // Clear history when switching versions
  useEffect(() => {
    if (activeVersion?.id !== activeVersionIdRef.current) {
      setHistory({ past: [], future: [] });
      activeVersionIdRef.current = activeVersion?.id || null;
    }
  }, [activeVersion?.id]);

  // Load Data from Firestore on Login
  useEffect(() => {
    if (user) {
      const fetchData = async () => {
        try {
          const [projects, config, learned] = await Promise.all([
            loadProjectsFromFirestore(user.uid),
            loadUserConfig(user.uid),
            loadLearnedElements(user.uid)
          ]);

          setAppState(prev => {
            const mergedProjects = [...(projects || [])];
            const projectsToUse = mergedProjects.length > 0 ? mergedProjects : prev.projects;

            // Auto-migrate version names to the new convention based on current language
            const currentLang = config?.language || prev.config.language;
            const migratedProjects = projectsToUse.map(p => ({
              ...p,
              scriptVersions: p.scriptVersions?.map(v => ({
                ...v,
                name: translateVersionName(v.name, currentLang)
              }))
            }));

            const activeId = prev.activeProjectId !== 'start-menu' ? prev.activeProjectId : (migratedProjects.length > 0 ? migratedProjects[0].id : 'start-menu');

            return {
              ...prev,
              projects: migratedProjects,
              config: config ? {
                ...prev.config,
                ...config,
                customMethods: config.customMethods || []
              } : prev.config,
              activeProjectId: activeId
            };
          });
          setLearnedElements(learned || {});
          setDataLoaded(true);
        } catch (error) {
          console.error("Error loading data from Firestore:", error);
          setDataLoaded(true); // Allow app to load even if empty
        }
      };
      fetchData();
    } else {
      // Reset state on logout
      setDataLoaded(true);
    }
  }, [user]);

  // Warm up PDF Worker
  useEffect(() => {
    getPdfParserWorker();
  }, []);

  // Save changes to Firestore and LocalStorage
  useEffect(() => {
    if (!dataLoaded) return;

    // LocalStorage Save (Every change, tied to user)
    const saveToLocal = () => {
      if (!user) return;
      try {
        const stateToSave = {
          projects: appState.projects,
          activeProjectId: appState.activeProjectId,
          config: appState.config
        };

        const pruned = pruneStateForStorage(stateToSave);
        const success = saveToLocalStorageChunked(getStorageKey(user.uid), pruned);

        if (!success) {
          throw new Error("Chunked save failed");
        }
      } catch (e) {
        console.warn("[Storage] LocalStorage quota exceeded. Using fallback pruning.", e);
        try {
          // Emergency pruning: Keep ONLY the active project and its active version
          const activeProj = appState.projects.find(p => p.id === appState.activeProjectId);
          if (activeProj) {
            const emergencyProj = {
              ...activeProj,
              scriptVersions: activeProj.scriptVersions.filter(v => v.id === activeProj.activeVersionId),
              chatHistory: []
            };
            const emergencyState = {
              projects: [emergencyProj],
              activeProjectId: appState.activeProjectId,
              config: appState.config
            };
            saveToLocalStorageChunked(getStorageKey(user.uid), emergencyState);
          }
        } catch (innerE) {
          console.error("[Storage] Critical failure saving to LocalStorage. Cleaning all non-active projects.", innerE);
          try {
            // Ultimate survival: CLEAR COMPLETELY except active version of active project
            const activeProj = appState.projects.find(p => p.id === appState.activeProjectId);
            if (activeProj) {
              const absoluteMinimumState = {
                projects: [{
                  ...activeProj,
                  scriptVersions: activeProj.scriptVersions.filter(v => v.id === activeProj.activeVersionId).map(v => ({ ...v, auditResult: undefined })),
                  chatHistory: []
                }],
                activeProjectId: appState.activeProjectId,
                config: appState.config
              };
              saveToLocalStorageChunked(getStorageKey(user.uid), absoluteMinimumState);
            }
          } catch (terminalE) {
            console.error("[Storage] Fatal storage failure.", terminalE);
          }
        }
      }
    };

    saveToLocal();

    // Firestore Save (Debounced, requires user)
    if (!user) return;

    const saveData = async () => {
      setSaveStatus('saving');
      try {
        await syncAppStateToFirestore(user.uid, appState.projects, appState.config);
        setSaveStatus('saved');
      } catch (error) {
        console.error("Error saving to Firestore:", error);
        setSaveStatus('idle');
      }
    };

    const timer = setTimeout(() => {
      saveData().then(() => {
        setTimeout(() => setSaveStatus('idle'), 2000);
      });
    }, 2000); // 2s debounce

    return () => clearTimeout(timer);
  }, [appState.projects, appState.config, appState.activeProjectId, user, dataLoaded]);

  // Handle Project Deletion explicitly to remove from Firestore
  useEffect(() => {
    if (!user) return;
    // This is tricky because appState is the truth. 
    // We need to compare with DB? No.
    // Best pattern: Update the handleDeleteProject function to call deleteProjectFromFirestore.
  }, []);

  const analysisResults = useScriptAnalysis(scriptBlocks, appState.config, activeProject?.manualOverrides, learnedElements);

  useEffect(() => {
    setAppState(prev => {
      // Avoid infinite loop by shallow checking if analysis results are actually different
      // We use a timestamp or a simple count to avoid heavy deep equals
      if (prev.analysis?.totalWords === analysisResults.totalWords &&
        prev.analysis?.scenes?.length === analysisResults.scenes?.length &&
        prev.analysis?.characters?.length === analysisResults.characters?.length) {
        return prev;
      }
      return { ...prev, analysis: analysisResults };
    });
  }, [analysisResults]);

  // --- INTERNAL UPDATE FUNCTION (Does the actual state change) ---
  const _internalUpdateScript = useCallback((blocks: ScriptBlock[]) => {
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId && p.activeVersionId) {
          const updatedVersions = p.scriptVersions.map(v =>
            v.id === p.activeVersionId ? { ...v, blocks: blocks } : v
          );
          return { ...p, scriptVersions: updatedVersions, lastModified: Date.now(), status: 'active' };
        }
        return p;
      });
      return { ...prev, projects };
    });
  }, []);

  // --- HISTORY EXECUTION ---
  const executeCommand = (command: Command) => {
    command.apply();
    setHistory(prev => {
      const newPast = [...prev.past, command];
      if (newPast.length > MAX_HISTORY_STACK) newPast.shift();
      return {
        past: newPast,
        future: [] // New action clears future
      };
    });
  };

  const handleUndo = useCallback(() => {
    setHistory(prev => {
      if (prev.past.length === 0) return prev;
      const newPast = [...prev.past];
      const command = newPast.pop();

      if (command) {
        command.revert();
        return {
          past: newPast,
          future: [command, ...prev.future]
        };
      }
      return prev;
    });
  }, []);

  const handleRedo = useCallback(() => {
    setHistory(prev => {
      if (prev.future.length === 0) return prev;
      const newFuture = [...prev.future];
      const command = newFuture.shift();

      if (command) {
        command.apply();
        return {
          past: [...prev.past, command],
          future: newFuture
        };
      }
      return prev;
    });
  }, []);

  // --- ACTION HANDLERS ---

  // Wrapper for updating script that creates a command
  const handleUpdateActiveScript = (newBlocks: ScriptBlock[], skipHistory: boolean = false) => {
    if (appState.activeProjectId === 'start-menu' || !activeVersion) return;

    if (skipHistory) {
      _internalUpdateScript(newBlocks);
      return;
    }

    const previousBlocks = activeVersion.blocks;

    // Command Pattern
    const command: Command = {
      id: `cmd - ${Date.now()} `,
      type: 'UPDATE_SCRIPT',
      timestamp: Date.now(),
      payload: { count: newBlocks.length },
      apply: () => _internalUpdateScript(newBlocks),
      revert: () => _internalUpdateScript(previousBlocks)
    };

    executeCommand(command);
  };

  // Helper to update a single block content by ID (Wrapped in Command)
  const handleUpdateBlock = (blockId: string, content: string) => {
    if (appState.activeProjectId === 'start-menu' || !activeVersion) return;

    const previousBlocks = activeVersion.blocks;
    const newBlocks = previousBlocks.map(b => b.id === blockId ? { ...b, content } : b);

    const command: Command = {
      id: `cmd - update - ${blockId} -${Date.now()} `,
      type: 'UPDATE_BLOCK',
      timestamp: Date.now(),
      payload: { blockId, content },
      apply: () => _internalUpdateScript(newBlocks),
      revert: () => _internalUpdateScript(previousBlocks)
    };

    executeCommand(command);
  };

  // Create Project Immediately (used for Import)
  const handleCreateProject = (mode: StageMode = 'landing', isPaste: boolean = false, initialBlocks?: ScriptBlock[], language?: Language, projectName?: string, authorName?: string, basedOn?: string, contactInfo?: string, genre?: string, description?: string, casting?: string[], formatId?: string, briefingAnswers?: Record<string, string>, renderMode?: 'master_scene' | 'dual_column') => {
    if (appState.projects.length >= MAX_PROJECTS_TRIAL) {
      setShowLimitModal(true);
      return;
    }

    const timestamp = Date.now();
    const versionId = `ver - ${timestamp} `;

    // Choose the best title: provided name (from metadata) or default translated title
    const defaultTitle = appState.config.language === 'pt' ? "Roteiro Importado" : "Imported Script";
    const finalProjectName = projectName || (initialBlocks ? defaultTitle : t.untitled);

    const newProject: Project = {
      id: `proj - ${timestamp} `,
      name: finalProjectName,
      author: authorName || "",
      basedOn: basedOn || "",
      contactInfo: contactInfo || "",
      genre: genre || "",
      description: description || "",
      status: 'active',
      blocks: [],
      scriptVersions: initialBlocks ? [{
        id: versionId,
        name: (() => {
          const lang = appState.config.language;
          if (lang === 'pt') return '1ª Versão';
          if (lang === 'es') return '1ª Versión';
          if (lang === 'fr') return '1ª Version';
          return '1ª Draft';
        })(),
        blocks: initialBlocks,
        language: language || (appState.config.language as Language),
        createdAt: timestamp
      }] : [],
      activeVersionId: initialBlocks ? versionId : undefined,
      chatHistory: [],
      characterProfiles: casting ? casting.reduce((acc, name) => {
        const id = `char-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        acc[name.toUpperCase()] = {
          id,
          name: name.toUpperCase(),
          role: 'Unknown',
          data: {
            identity: { full_name: name, age_range: '', profession_or_role: '', social_class: '', era_setting: '', current_location: '', is_real_person: false, is_inferred: true },
            psychology: { archetype: '', personality_traits: [], moral_code_or_stance: '', core_fears_or_pains: '', primary_goal: '' },
            expression: { speech_style: '', conversation_topics: [], sense_of_humor: '', visual_description: '' }
          }
        };
        return acc;
      }, {} as Record<string, CharacterProfile>) : {},
      locationProfiles: {},
      propProfiles: {},
      verifiedSegments: [], // Phase 7: Initialize verified segments
      createdAt: timestamp,
      lastModified: timestamp,
      formatId,
      briefingAnswers,
      renderMode
    };

    setAppState(prev => ({
      ...prev,
      projects: [...prev.projects, newProject],
      activeProjectId: newProject.id
    }));

    // Set immediate mode
    const targetMode = initialBlocks ? 'editor' : mode;
    setProjectStartMode({ mode: targetMode, isPaste });
  };

  // Configure Project: Opens Modal for specific modes (MainStage Action)
  const handleConfigureProject = (mode: StageMode, isPaste: boolean = false) => {
    // First check limits
    if (appState.projects.length >= MAX_PROJECTS_TRIAL) {
      setShowLimitModal(true);
      return;
    }

    setPendingStartMode({ mode, isPaste });
    setIsEditingTitlePage(false);

    // Always reset for new project via Start Menu
    setNewProjectTitle("");
    setNewProjectAuthor("");
    setNewProjectBasedOn("");
    setNewProjectContactInfo("");

    setShowNewProjectModal(true);
  };

  const handleEditTitlePage = () => {
    if (!activeProject) return;
    setNewProjectTitle(activeProject.name || "");
    setNewProjectAuthor(activeProject.author || "");
    setNewProjectBasedOn(activeProject.basedOn || "");
    setNewProjectContactInfo(activeProject.contactInfo || "");
    setIsEditingTitlePage(true);
    setShowNewProjectModal(true);
  };

  // Confirm Setup: Creates new project with metadata
  const confirmProjectSetup = () => {
    if (isEditingTitlePage && activeProject) {
      // Edit mode: Update project metadata and script blocks
      const authorCredit = appState.config.language === 'pt' ? 'Escrito por' :
        appState.config.language === 'es' ? 'Escrito por' :
          appState.config.language === 'fr' ? 'Écrit par' : 'Written by';

      const newTitleBlocks = createTitlePageBlocks(
        newProjectTitle.trim() || t.untitled,
        newProjectAuthor.trim(),
        authorCredit,
        newProjectBasedOn.trim(),
        newProjectContactInfo.trim()
      );

      setAppState(prev => ({
        ...prev,
        projects: prev.projects.map(p => {
          if (p.id === activeProject.id) {
            const updatedProject = {
              ...p,
              name: newProjectTitle.trim() || t.untitled,
              author: newProjectAuthor.trim(),
              basedOn: newProjectBasedOn.trim(),
              contactInfo: newProjectContactInfo.trim(),
              lastModified: Date.now()
            };

            if (updatedProject.scriptVersions) {
              updatedProject.scriptVersions = updatedProject.scriptVersions.map(v => {
                const nonMetadataBlocks = v.blocks.filter(b => !b.metadata);
                return {
                  ...v,
                  blocks: [...newTitleBlocks, ...nonMetadataBlocks]
                };
              });
            }
            return updatedProject;
          }
          return p;
        })
      }));

      setIsEditingTitlePage(false);
      setShowNewProjectModal(false);
      return;
    }

    // Create New Project logic
    const newProject: Project = {
      id: `proj-${Date.now()}`,
      name: newProjectTitle.trim() || t.untitled,
      author: newProjectAuthor.trim(),
      basedOn: newProjectBasedOn.trim(),
      contactInfo: newProjectContactInfo.trim(),
      status: 'active',
      blocks: [],
      scriptVersions: [],
      chatHistory: [],
      characterProfiles: {},
      locationProfiles: {},
      propProfiles: {},
      verifiedSegments: [],
      createdAt: Date.now(),
      lastModified: Date.now(),
      formatId: appState.activeFormatId || 'cinema',
      briefingAnswers: appState.activeBriefingAnswers || {}
    };

    setAppState(prev => ({
      ...prev,
      projects: [...prev.projects, newProject],
      activeProjectId: newProject.id
    }));

    if (pendingStartMode) {
      setProjectStartMode(pendingStartMode);
    }
    setShowNewProjectModal(false);
  };

  const handleRenameProject = (id: string, newName: string) => {
    setAppState(prev => ({
      ...prev,
      projects: prev.projects.map(p => p.id === id ? { ...p, name: newName, lastModified: Date.now() } : p)
    }));
  };

  const handleDeleteProject = (idToDelete: string) => {
    // Optimistic Update
    setAppState(prev => {
      const remainingProjects = prev.projects.filter(p => p.id !== idToDelete);

      let newActiveId = prev.activeProjectId;
      let stateResets = {};

      if (idToDelete === prev.activeProjectId) {
        newActiveId = 'start-menu';
        stateResets = {
          activeFormatId: undefined,
          activeBriefingAnswers: {}
        };
      }

      return {
        ...prev,
        ...stateResets,
        projects: remainingProjects,
        activeProjectId: newActiveId
      };
    });

    // Firestore Delete
    if (user) {
      deleteProjectFromFirestore(user.uid, idToDelete).catch(err => console.error("Failed to delete project from DB:", err));
    }
  };

  const handleUpdateProject = (id: string, updates: Partial<Project>) => {
    setAppState(prev => ({
      ...prev,
      projects: prev.projects.map(p => p.id === id ? { ...p, ...updates, lastModified: Date.now() } : p)
    }));
  };

  const handleSwitchProject = (id: string) => {
    setAppState(prev => ({
      ...prev,
      activeProjectId: id,
      activeFormatId: id === 'start-menu' ? undefined : prev.activeFormatId,
      activeBriefingAnswers: id === 'start-menu' ? {} : prev.activeBriefingAnswers
    }));
  };

  // ... (Update Profile Handlers remain same, but need check for activeProject existence) ...
  const handleUpdateCharacterProfile = (oldName: string, newName: string, profile: CharacterProfile) => {
    if (appState.activeProjectId === 'start-menu') return;

    const normalizedOld = oldName.trim().toUpperCase();
    const normalizedNew = newName.trim().toUpperCase();

    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const newProfiles = { ...(p.characterProfiles || {}) };
          const newOverrides = { ...(p.manualOverrides || {}) };

          // 1. Update Profile Registry
          if (normalizedOld !== normalizedNew) {
            delete newProfiles[normalizedOld];
            delete newOverrides[normalizedOld];
          }

          // Ensure the profile object itself has the new name normalized
          const updatedProfile = {
            ...profile,
            name: normalizedNew,
            isModified: profile.isModified ?? false
          };

          newProfiles[normalizedNew] = updatedProfile;
          newOverrides[normalizedNew] = 'CHARACTER';

          // 2. Comprehensive Script Update (Find & Replace)
          let updatedScriptVersions = p.scriptVersions;
          if (normalizedOld !== normalizedNew) {
            // Robust regex: matches name with word boundaries, ignoring case
            // Handles "MARCO", "MARCO (V.O.)", "MARCO entra na sala", etc.
            const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const leftB = '(^|[^a-zA-Z0-9À-ÖØ-öø-ÿ_])';
            const rightB = '($|[^a-zA-Z0-9À-ÖØ-öø-ÿ_])';
            const nameRegex = new RegExp(`${leftB}${escapeRegExp(normalizedOld)}${rightB}`, 'gi');

            updatedScriptVersions = p.scriptVersions.map(v => {
              // We primarily update the active version to ensure immediate feedback in the UI list
              if (v.id === p.activeVersionId) {
                const newBlocks = v.blocks.map(block => {
                  if (nameRegex.test(block.content)) {
                    // $1 is the prefix (boundary), $2 is the suffix (boundary)
                    return { ...block, content: block.content.replace(nameRegex, `$1${normalizedNew}$2`) };
                  }
                  return block;
                });
                return { ...v, blocks: newBlocks };
              }
              return v;
            });
          }

          return {
            ...p,
            characterProfiles: newProfiles,
            manualOverrides: newOverrides,
            scriptVersions: updatedScriptVersions,
            lastModified: Date.now()
          };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleUpdateCharacterProfilesBulk = (updates: CharacterProfile[]) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const newProfiles = { ...(p.characterProfiles || {}) };
          const newOverrides = { ...(p.manualOverrides || {}) };

          updates.forEach(profile => {
            const name = profile.name.trim().toUpperCase();
            newProfiles[name] = { ...profile, isModified: profile.isModified ?? false };
            newOverrides[name] = 'CHARACTER';
          });

          return { ...p, characterProfiles: newProfiles, manualOverrides: newOverrides, lastModified: Date.now() };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleResetCharactersModified = () => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const newProfiles = { ...(p.characterProfiles || {}) };
          Object.keys(newProfiles).forEach(name => {
            if (newProfiles[name].isModified) {
              newProfiles[name] = { ...newProfiles[name], isModified: false };
            }
          });
          return { ...p, characterProfiles: newProfiles, lastModified: Date.now() };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };


  const handleUpdateLocationProfile = (oldName: string, newName: string, description: string) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const newLocProfiles = { ...(p.locationProfiles || {}) };
          const newOverrides = { ...(p.manualOverrides || {}) };
          if (oldName !== newName) {
            delete newLocProfiles[oldName];
            delete newOverrides[oldName];
          }
          newLocProfiles[newName] = description;
          newOverrides[newName] = 'LOCATION';

          return { ...p, locationProfiles: newLocProfiles, manualOverrides: newOverrides, lastModified: Date.now() };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleUpdatePropProfile = (oldName: string, newName: string, description: string) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const newPropProfiles = { ...(p.propProfiles || {}) };
          const newOverrides = { ...(p.manualOverrides || {}) };
          if (oldName !== newName) {
            delete newPropProfiles[oldName];
            delete newOverrides[oldName];
          }
          newPropProfiles[newName] = description;
          newOverrides[newName] = 'PROP';

          return { ...p, propProfiles: newPropProfiles, manualOverrides: newOverrides, lastModified: Date.now() };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleUpdateSfxProfile = (oldName: string, newName: string, description: string) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const newSfxProfiles = { ...(p.sfxProfiles || {}) };
          const newOverrides = { ...(p.manualOverrides || {}) };
          if (oldName !== newName) {
            delete newSfxProfiles[oldName];
            delete newOverrides[oldName];
          }
          newSfxProfiles[newName] = description;
          newOverrides[newName] = 'SFX_VFX';
          return { ...p, sfxProfiles: newSfxProfiles, manualOverrides: newOverrides, lastModified: Date.now() };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleUpdateCameraProfile = (oldName: string, newName: string, description: string) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const newCameraProfiles = { ...(p.cameraProfiles || {}) };
          const newOverrides = { ...(p.manualOverrides || {}) };
          if (oldName !== newName) {
            delete newCameraProfiles[oldName];
            delete newOverrides[oldName];
          }
          newCameraProfiles[newName] = description;
          newOverrides[newName] = 'CAMERA';
          return { ...p, cameraProfiles: newCameraProfiles, manualOverrides: newOverrides, lastModified: Date.now() };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleUpdateTransitionProfile = (oldName: string, newName: string, description: string) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const newTransProfiles = { ...(p.transitionProfiles || {}) };
          const newOverrides = { ...(p.manualOverrides || {}) };
          if (oldName !== newName) {
            delete newTransProfiles[oldName];
            delete newOverrides[oldName];
          }
          newTransProfiles[newName] = description;
          newOverrides[newName] = 'TRANSITION';
          return { ...p, transitionProfiles: newTransProfiles, manualOverrides: newOverrides, lastModified: Date.now() };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleUpdateVerifiedSegments = (newSegments: string[], shouldReplace: boolean = false) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          // Merge active segments with new ones, avoiding duplicates, OR replace if requested
          const currentSegments = shouldReplace ? new Set<string>() : new Set(p.verifiedSegments || []);
          newSegments.forEach(s => currentSegments.add(s));
          return {
            ...p,
            verifiedSegments: Array.from(currentSegments),
            lastModified: Date.now()
          };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleCheckConsistency = async (paragraph: string) => {
    const profs = activeProject?.characterProfiles;
    if (!profs) return;

    const bible: CharacterProfile[] = Object.values(profs);
    if (!bible || bible.length === 0) return;

    try {
      const inconsistentNames = await checkCharacterConsistency(paragraph, bible as CharacterProfile[], appState.config);

      if (inconsistentNames && inconsistentNames.length > 0) {
        toast.warning(appState.config.language === 'pt' ? 'Inconsistência de personagem detectada!' : 'Character inconsistency detected!', {
          description: appState.config.language === 'pt' ? `Verifique: ${inconsistentNames.join(', ')}` : `Check: ${inconsistentNames.join(', ')}`,
        });

        setAppState(prev => {
          const newProjects = prev.projects.map(p => {
            if (p.id === prev.activeProjectId) {
              const newProfiles = { ...(p.characterProfiles || {}) };
              inconsistentNames.forEach(name => {
                const norm = name.trim().toUpperCase();
                if (newProfiles[norm]) {
                  newProfiles[norm] = { ...newProfiles[norm], isModified: true };
                }
              });
              return { ...p, characterProfiles: newProfiles, lastModified: Date.now() };
            }
            return p;
          });
          return { ...prev, projects: newProjects };
        });
      }
    } catch (error) {
      console.error("Consistency check failed:", error);
    }
  };

  // Normalize category names to prevent duplicates
  const normalizeCategoryName = (category: string): string => {
    const normalized = category.trim().toUpperCase();

    // Map common variations to standard category IDs
    const categoryMap: Record<string, string> = {
      // SFX/VFX variations
      'SFX/VFX EFFECTS': 'SFX_VFX',
      'SFX/VFX': 'SFX_VFX',
      'SFXVFX': 'SFX_VFX',
      'SFX': 'SFX_VFX',
      'VFX': 'SFX_VFX',
      'EFFECTS': 'SFX_VFX',
      'EFEITOS': 'SFX_VFX',
      'SFX_VFX': 'SFX_VFX',

      // Character variations
      'CHARACTERS': 'CHARACTER',
      'PERSONAGENS': 'CHARACTER',
      'PERSONAGEM': 'CHARACTER',
      'CHARACTER': 'CHARACTER',

      // Location variations
      'LOCATIONS': 'LOCATION',
      'LOCAÇÕES': 'LOCATION',
      'LOCAÇÃO': 'LOCATION',
      'LOCATION': 'LOCATION',

      // Prop variations
      'PROPS': 'PROP',
      'ADEREÇOS': 'PROP',
      'ADEREÇO': 'PROP',
      'PROP': 'PROP',

      // Camera variations
      'CAMERAS': 'CAMERA',
      'CÂMERAS': 'CAMERA',
      'CÂMERA': 'CAMERA',
      'DIREÇÃO DE CÂMERAS': 'CAMERA',
      'CAMERA': 'CAMERA',

      // Transition variations
      'TRANSITIONS': 'TRANSITION',
      'TRANSIÇÕES': 'TRANSITION',
      'TRANSIÇÃO': 'TRANSITION',
      'TRANSITION': 'TRANSITION',

      // Ignore
      'IGNORE': 'IGNORE',
      'IGNORAR': 'IGNORE'
    };

    // Check for exact match first
    if (categoryMap[normalized]) {
      return categoryMap[normalized];
    }

    // Check for partial matches (e.g., "SFX/VFX Effects" contains "SFX/VFX")
    for (const [key, value] of Object.entries(categoryMap)) {
      if (normalized.includes(key) || key.includes(normalized)) {
        return value;
      }
    }

    // If no match found, return the original category (allows custom categories)
    return category;
  };

  const handleRecategorizeElement = (elementName: string, newCategory: string) => {
    if (appState.activeProjectId === 'start-menu') return;

    // Normalize the category to prevent duplicates
    const normalizedCategory = normalizeCategoryName(newCategory);

    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const newOverrides = { ...(p.manualOverrides || {}) };
          newOverrides[elementName.toUpperCase()] = normalizedCategory;
          return { ...p, manualOverrides: newOverrides, lastModified: Date.now() };
        }
        return p;
      });
      return { ...prev, projects };
    });

    // Also sync to User Global Knowledge Base
    if (user) {
      saveLearnedElement(user.uid, elementName, normalizedCategory).catch(err => console.error("Failed to save learned element:", err));
      setLearnedElements(prev => ({
        ...prev,
        [elementName.toUpperCase()]: normalizedCategory
      }));
    }
  };

  const handleCategorizeElements = (items: { name: string, category: string }[]) => {
    if (appState.activeProjectId === 'start-menu') return;

    setAppState(prev => {
      try {
        const projects = prev.projects.map(p => {
          if (p.id === prev.activeProjectId) {
            // Checkpoint A: Initial state
            const initialOverrideCount = Object.keys(p.manualOverrides || {}).length;
            console.log(`[Checkpoint A] Initial elements in overrides: ${initialOverrideCount} `);

            const newOverrides = { ...(p.manualOverrides || {}) };

            // Sync with official profiles (Source of Truth)
            const newCharProfiles = { ...(p.characterProfiles || {}) };
            const newLocProfiles = { ...(p.locationProfiles || {}) };
            const newPropProfiles = { ...(p.propProfiles || {}) };
            const newSfxProfiles = { ...(p.sfxProfiles || {}) };
            const newCameraProfiles = { ...(p.cameraProfiles || {}) };
            const newTransProfiles = { ...(p.transitionProfiles || {}) };

            items.forEach(item => {
              if (!item?.name) return;
              const norm = item.name.trim().toUpperCase();
              // Normalize category to prevent duplicates
              const cat = normalizeCategoryName(item.category);

              // Update Overrides
              newOverrides[norm] = cat;

              // Update Official Profiles (Empty profile if new)
              if (cat === 'CHARACTER' && !newCharProfiles[norm]) {
                newCharProfiles[norm] = {
                  id: generateUUID(),
                  name: norm,
                  role: 'Coadjuvante',
                  data: {
                    identity: { full_name: '', age_range: '', profession_or_role: '', social_class: '', era_setting: '', current_location: '', is_real_person: false, is_inferred: true },
                    psychology: { archetype: '', personality_traits: [], moral_code_or_stance: '', core_fears_or_pains: '', primary_goal: '' },
                    expression: { speech_style: '', conversation_topics: [], sense_of_humor: '', visual_description: '' }
                  }
                };
              }
              if (cat === 'LOCATION' && !newLocProfiles[norm]) newLocProfiles[norm] = "";
              if (cat === 'PROP' && !newPropProfiles[norm]) newPropProfiles[norm] = "";
              if (cat === 'SFX_VFX' && !newSfxProfiles[norm]) newSfxProfiles[norm] = "";
              if (cat === 'CAMERA' && !newCameraProfiles[norm]) newCameraProfiles[norm] = "";
              if (cat === 'TRANSITION' && !newTransProfiles[norm]) newTransProfiles[norm] = "";

              // Sync to Global Knowledge Base
              if (user) {
                saveLearnedElement(user.uid, norm, cat).catch(err => console.error("Failed to save learned element in batch:", err));
              }
            });

            // Update Local state for global knowledge too
            if (user) {
              setLearnedElements(prevLearned => {
                const next = { ...prevLearned };
                items.forEach(item => {
                  next[item.name.trim().toUpperCase()] = normalizeCategoryName(item.category);
                });
                return next;
              });
            }

            // Checkpoint B: Verify insertion
            const finalOverrideCount = Object.keys(newOverrides).length;
            const success = items.every(item => newOverrides[item.name.trim().toUpperCase()] === normalizeCategoryName(item.category));
            console.log(`[Checkpoint B] Final elements: ${finalOverrideCount}.Success: ${success} `);

            if (!success) {
              throw new Error("Failed to update official source (manualOverrides).");
            }

            return {
              ...p,
              manualOverrides: newOverrides,
              characterProfiles: newCharProfiles,
              locationProfiles: newLocProfiles,
              propProfiles: newPropProfiles,
              sfxProfiles: newSfxProfiles,
              cameraProfiles: newCameraProfiles,
              transitionProfiles: newTransProfiles,
              lastModified: Date.now()
            };
          }
          return p;
        });
        return { ...prev, projects };
      } catch (e) {
        console.error("Critical error in handleCategorizeElements:", e);
        return prev;
      }
    });
  };

  // --- CUSTOM KNOWLEDGE BASE HANDLERS ---
  const handleAddCustomMethod = async (name: string, content: string, signature?: string) => {
    const newMethod = { id: `cm - ${Date.now()} `, name, content, isActive: true, signature };

    setAppState(prev => ({
      ...prev,
      config: { ...prev.config, customMethods: [...(prev.config.customMethods || []), newMethod] }
    }));

    if (user) {
      try {
        await addCustomMethodToFirestore(user.uid, newMethod);
      } catch (e) {
        console.error("Failed to add custom method to DB:", e);
      }
    }
  };

  const handleToggleCustomMethod = async (id: string) => {
    let updatedMethod: any = null;

    setAppState(prev => {
      const methods = prev.config.customMethods || [];
      const newMethods = methods.map(m => {
        if (m.id === id) {
          updatedMethod = { ...m, isActive: !m.isActive };
          return updatedMethod;
        }
        return m;
      });
      return {
        ...prev,
        config: { ...prev.config, customMethods: newMethods }
      };
    });

    if (user && updatedMethod) {
      try {
        await updateCustomMethodInFirestore(user.uid, updatedMethod);
      } catch (e) {
        console.error("Failed to update custom method in DB:", e);
      }
    }
  };

  const handleDeleteCustomMethod = async (id: string) => {
    setAppState(prev => ({
      ...prev,
      config: { ...prev.config, customMethods: (prev.config.customMethods || []).filter(m => m.id !== id) }
    }));

    if (user) {
      try {
        await deleteCustomMethodFromFirestore(user.uid, id);
      } catch (e) {
        console.error("Failed to delete custom method from DB:", e);
      }
    }
  };

  const handleAddScriptVersion = (blocks: ScriptBlock[], metadata?: string, customName?: string, originChatId?: string, originMessageId?: string, sessionId?: string, language?: Language) => {
    if (appState.activeProjectId === 'start-menu') {
      const title = metadata?.match(/TITLE:\s*(.*)/)?.[1] || 'New Script';
      handleCreateProject('editor', false, blocks, language || (appState.config.language as Language), title, undefined, undefined, undefined, undefined, undefined, undefined, appState.activeFormatId, appState.activeBriefingAnswers, availableFormats.find(f => f.id === appState.activeFormatId)?.output_format as any);
      return;
    }

    setAppState(prev => {
      const currentProject = prev.projects.find(p => p.id === prev.activeProjectId);
      if (!currentProject) return prev;

      // 🛡️ ANTI-DUPLICATION SHIELD (State-Aware) 🛡️
      const currentActiveVersion = currentProject.scriptVersions.find(v => v.id === currentProject.activeVersionId);
      if (currentActiveVersion) {
        const currentContent = currentActiveVersion.blocks.map(b => b.content).join('|||');
        const newBlocksContent = blocks.map(b => b.content).join('|||');

        if (currentContent === newBlocksContent) {
          console.log("[Version Guard] Suppression system activated: New content matches existing state. Skipping version creation.");
          return prev;
        }
      }

      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const count = p.scriptVersions ? p.scriptVersions.length : 0;
          const lang = prev.config.language;
          const currentNum = count + 1;
          let baseName = 'Draft';
          if (lang === 'pt') baseName = 'Versão';
          if (lang === 'es') baseName = 'Versión';
          if (lang === 'fr') baseName = 'Version';

          const newName = customName || `${currentNum}ª ${baseName}`;

          const newVersion: ScriptVersion = {
            id: `ver-${Date.now()}`,
            name: newName,
            blocks: [...blocks],
            language: language || currentActiveVersion?.language || (prev.config.language as Language),
            infoMetadata: metadata || "",
            createdAt: Date.now(),
            originChatId,
            originMessageId,
            sessionId: sessionId || `session-${Date.now()}`,
            chatTouched: false
          };

          return {
            ...p,
            scriptVersions: [...(p.scriptVersions || []), newVersion],
            activeVersionId: newVersion.id,
            lastModified: Date.now()
          };
        }
        return p;
      });

      return { ...prev, projects };
    });
  };

  const handleUpdateVersionMetadata = (versionId: string, infoMetadata: string) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const updatedVersions = p.scriptVersions.map(v =>
            v.id === versionId ? { ...v, infoMetadata } : v
          );
          return { ...p, scriptVersions: updatedVersions, lastModified: Date.now() };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleUpdateDetectedFormat = (format: string) => {
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          return { ...p, detectedFormat: format, lastModified: Date.now() };
        }
        return p;
      });

      if (user) {
        syncAppStateToFirestore(user.uid, projects, prev.config);
      }

      return { ...prev, projects };
    });
  };

  const handleSwitchScriptVersion = (versionId: string) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const activeProj = prev.projects.find(p => p.id === prev.activeProjectId);
      const targetVersion = activeProj?.scriptVersions.find(v => v.id === versionId);

      return {
        ...prev,
        activeAnalysisTask: targetVersion?.auditResult ? { ...targetVersion.auditResult, isRestored: true } : null,
        projects: prev.projects.map(p =>
          p.id === prev.activeProjectId ? { ...p, activeVersionId: versionId } : p
        )
      };
    });
  };

  const handleRenameScriptVersion = (versionId: string, newName: string) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const updatedVersions = p.scriptVersions.map(v =>
            v.id === versionId ? { ...v, name: newName } : v
          );
          return { ...p, scriptVersions: updatedVersions };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const isInjectingRef = useRef(false);

  const handleInjectPlotFix = async (targetSceneIndex: number, chosenBeat: string) => {
    if (!activeProject || !activeVersion || activeVersion.blocks.length === 0) return;

    if (isInjectingRef.current) return;
    isInjectingRef.current = true;
    setIsPlotInjecting(true);

    try {
      // 1. Isolate the boundaries of the scene
      const startIndex = targetSceneIndex;
      let endIndex = activeVersion.blocks.length;

      // Find the start of the next scene to delimit the cut
      for (let i = startIndex + 1; i < activeVersion.blocks.length; i++) {
        const type = activeVersion.blocks[i].type;
        if (type === 'scene-heading') {
          endIndex = i;
          break;
        }
      }

      const sceneBlocks = activeVersion.blocks.slice(startIndex, endIndex);
      const sceneText = serializeBlocksToFountain(sceneBlocks);

      // 2. Call AI Service
      const userLang = appState.config.language === 'pt' ? 'Portuguese' :
        appState.config.language === 'es' ? 'Spanish' :
          appState.config.language === 'fr' ? 'French' : 'English';

      const rewrittenSceneText = await injectPlotFix(sceneText, chosenBeat, userLang);

      if (!rewrittenSceneText) throw new Error("AI returned empty rewritten text");

      // 3. Parse back to blocks
      const newSceneBlocks = parseTextToBlocks(rewrittenSceneText);

      // 4. Inject into main state
      const updatedBlocks = [...activeVersion.blocks];
      updatedBlocks.splice(startIndex, endIndex - startIndex, ...newSceneBlocks);

      const changeLabel = appState.config.language === 'pt'
        ? "Correção de trama injetada com sucesso"
        : "Plot fix successfully injected";

      handleAddScriptVersion(updatedBlocks, changeLabel);
      toast.success(changeLabel);

    } catch (error) {
      console.error('Failed to inject plot fix:', error);
      toast.error(appState.config.language === 'pt' ? 'Erro ao injetar correção' : 'Error injecting fix');
      throw error;
    } finally {
      isInjectingRef.current = false;
      setIsPlotInjecting(false);
    }
  };

  const handleGenerateFixOptions = async (factList: string[], plotHole: string, userLanguage: string) => {
    if (!activeProject || !activeVersion) return [];
    // The AiTaskModal provides the facts from the plot audit data causality chain
    return await generatePlotFixOptions(factList, plotHole, userLanguage);
  };

  const handleDeleteScriptVersion = (versionId: string) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const remainingVersions = p.scriptVersions.filter(v => v.id !== versionId);
          let nextActiveId = p.activeVersionId;
          if (p.activeVersionId === versionId) {
            nextActiveId = remainingVersions.length > 0 ? remainingVersions[remainingVersions.length - 1].id : undefined;
          }
          return {
            ...p,
            scriptVersions: remainingVersions,
            activeVersionId: nextActiveId
          };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const handleChatUpdate = (history: ChatMessage[]) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => ({
      ...prev,
      projects: prev.projects.map(p =>
        p.id === prev.activeProjectId
          ? { ...p, chatHistory: history, lastModified: Date.now() }
          : p
      )
    }));
  }

  const handleUpdateChatTouched = (versionId: string, touched: boolean) => {
    if (appState.activeProjectId === 'start-menu') return;
    setAppState(prev => {
      const projects = prev.projects.map(p => {
        if (p.id === prev.activeProjectId) {
          const updatedVersions = p.scriptVersions.map(v =>
            v.id === versionId ? { ...v, chatTouched: touched } : v
          );
          return { ...p, scriptVersions: updatedVersions };
        }
        return p;
      });
      return { ...prev, projects };
    });
  };

  const isGeneratingRef = useRef(false);

  const handleApplySuggestion = async (suggestion: string, taskType?: string, targetSceneText?: string) => {
    if (!activeProject || !activeVersion || activeVersion.blocks.length === 0) return;
    if (activeProject.scriptVersions && activeProject.scriptVersions.length >= 24) {
      alert(t.version_limit_reached);
      return;
    }

    // Strict synchronous guard against double-clicks
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    setIsGeneratingVersion(true);

    try {
      const firstSceneIdx = activeVersion.blocks.findIndex(b => b.type === 'scene-heading');
      const bodyBlocks = firstSceneIdx >= 0 ? activeVersion.blocks.slice(firstSceneIdx) : activeVersion.blocks;
      const currentScript = bodyBlocks.map(b => b.content).join('\n');

      // ====================================================================
      // CAMINHO CIRÚRGICO: Auditoria de Trama (Plot Audit) — Micro-Injeção
      // Intercepta plotaudit e processa cada fix individualmente por cena,
      // usando injectPlotFix com apenas o texto da cena afetada.
      // Isso evita enviar o roteiro inteiro, prevenindo:
      //   - Race Conditions (duplicação no AST)
      //   - HTTP 503 / Timeout (payload massivo)
      // ====================================================================
      if (taskType === 'plotaudit' || suggestion.includes('APPLY_PLOT_AUDIT_FIXES')) {
        console.log('[PlotAudit] Surgical micro-injection path detected');

        try {
          // 1. Parse the compiled fixes from the payload
          const jsonPart = suggestion.replace(/^APPLY_PLOT_AUDIT_FIXES:\n?/, '').trim();
          const compiledFixes: { hole: string; fix: { id: string; title: string; action_beat: string; insertion_point: string; ripple_effect: string } }[] = JSON.parse(jsonPart);

          if (!compiledFixes || compiledFixes.length === 0) {
            toast.info(appState.config.language === 'pt' ? 'Nenhuma correção para aplicar.' : 'No fixes to apply.');
            return;
          }

          // 2. Clone the full AST (we mutate this clone sequentially)
          let astClone = [...activeVersion.blocks];

          // Helper: get language label for injectPlotFix
          const langLabel = appState.config.language === 'pt' ? 'Portuguese (Brazil)'
            : appState.config.language === 'es' ? 'Spanish'
              : appState.config.language === 'fr' ? 'French'
                : 'English';

          // Helper: fuzzy match insertion_point against scene-heading blocks
          const normalizeForMatch = (s: string) => s.toLowerCase().replace(/[^a-záàãâéèêíóòõôúüç0-9\s]/gi, '').trim();

          const findTargetSceneIndex = (blocks: ScriptBlock[], insertionPoint: string): number => {
            const sceneIndices = blocks.map((b, i) => b.type === 'scene-heading' ? i : -1).filter(i => i >= 0);
            if (sceneIndices.length === 0) return 0;

            const target = normalizeForMatch(insertionPoint);
            let bestIdx = sceneIndices[sceneIndices.length - 1]; // fallback: last scene
            let bestScore = 0;

            for (const idx of sceneIndices) {
              const heading = normalizeForMatch(blocks[idx].content);
              const targetWords = target.split(/\s+/);
              const score = targetWords.filter(w => w.length > 2 && heading.includes(w)).length;
              if (score > bestScore) {
                bestScore = score;
                bestIdx = idx;
              }
            }
            return bestIdx;
          };

          // Helper: get scene bounds [startIdx, endIdx) exclusive
          const getSceneBounds = (blocks: ScriptBlock[], sceneStartIdx: number): [number, number] => {
            const nextScene = blocks.findIndex((b, i) => i > sceneStartIdx && b.type === 'scene-heading');
            const endIdx = nextScene >= 0 ? nextScene : blocks.length;
            return [sceneStartIdx, endIdx];
          };

          // 3. Sequential processing — one fix at a time to avoid race conditions
          let successCount = 0;
          for (const item of compiledFixes) {
            const { fix } = item;
            if (!fix || !fix.action_beat) continue;

            try {
              // a. Find the target scene via fuzzy match on insertion_point
              const sceneIdx = findTargetSceneIndex(astClone, fix.insertion_point);
              const [sceneStart, sceneEnd] = getSceneBounds(astClone, sceneIdx);
              const sceneBlocks = astClone.slice(sceneStart, sceneEnd);

              console.log(`[PlotAudit] Injecting fix "${fix.title}" into scene at index ${sceneIdx} (blocks ${sceneStart}-${sceneEnd})`);

              // b. Serialize only the scene slice to Fountain text
              const sceneText = serializeBlocksToFountain(sceneBlocks);

              // c. Call injectPlotFix with just the scene + beat
              const rewrittenSceneText = await injectPlotFix(sceneText, fix.action_beat, langLabel);

              // d. Parse the rewritten Fountain back to blocks and mark as AI suggestion
              const newSceneBlocks = parseTextToBlocks(rewrittenSceneText).map(b => ({
                ...b,
                isAiSuggestion: true
              }));

              // e. Splice: replace old scene blocks with new ones in the AST clone
              astClone.splice(sceneStart, sceneEnd - sceneStart, ...newSceneBlocks);

              successCount++;
            } catch (fixErr) {
              console.error(`[PlotAudit] Failed to inject fix "${fix.title}":`, fixErr);
              toast.error(
                appState.config.language === 'pt'
                  ? `Falha ao injetar: "${fix.title}". Pulando...`
                  : `Failed to inject: "${fix.title}". Skipping...`
              );
            }
          }

          // 4. Single state update after all fixes are applied
          if (successCount > 0) {
            const changeLabel = appState.config.language === 'pt'
              ? `Correções de trama aplicadas (${successCount} ${successCount === 1 ? 'cena' : 'cenas'})`
              : `Plot fixes applied (${successCount} ${successCount === 1 ? 'scene' : 'scenes'})`;

            // Enable line pagination for plot audit fixes
            setAppState(prev => ({
              ...prev,
              config: { ...prev.config, linePagination: true }
            }));

            handleAddScriptVersion(astClone, changeLabel);
          } else {
            toast.error(
              appState.config.language === 'pt'
                ? 'Nenhuma correção de trama foi aplicada com sucesso.'
                : 'No plot fixes were successfully applied.'
            );
          }
        } catch (parseErr) {
          console.error('[PlotAudit] Failed to parse compiled fixes:', parseErr);
          toast.error(
            appState.config.language === 'pt'
              ? 'Erro ao processar as correções de trama.'
              : 'Error processing plot fixes.'
          );
        }

        return; // NEVER proceed to updateScriptWithSuggestion
      }

      // ====================================================================
      // TEMPO 2 OPTIMIZATION: Direct AST Mutation for APPLY_AUDIT_FIXES
      // When issues carry target_block_indices from the Tempo 1 review,
      // we apply corrections via direct O(1) block mutation — NO AI call.
      // ====================================================================
      if (suggestion.includes('APPLY_AUDIT_FIXES')) {
        try {
          const jsonPart = suggestion.replace(/^APPLY_AUDIT_FIXES:\n?/, '').trim();
          const issues: any[] = JSON.parse(jsonPart);

          // Check if ALL issues have target_block_indices (the new O(1) path)
          const allHaveIndices = issues.length > 0 && issues.every(
            (issue: any) => Array.isArray(issue.target_block_indices) && issue.target_block_indices.length > 0
          );

          if (allHaveIndices) {
            console.log('[Tempo2] Direct AST mutation path. Issues:', issues.length);

            // Clone the AST (shallow copy of array, each block gets cloned only when modified)
            const newBlocks = [...activeVersion.blocks];
            // Track blocks that have been modified to avoid double-patching
            const modifiedIndices = new Set<number>();

            // Sort issues by descending target_block_indices to handle splice correctly
            // (inserting from bottom to top prevents index shifts)
            const sortedIssues = [...issues].sort((a, b) => {
              const aMax = Math.max(...a.target_block_indices);
              const bMax = Math.max(...b.target_block_indices);
              return bMax - aMax;
            });

            for (const issue of sortedIssues) {
              const { target_block_indices, suggestion: suggestedText, rule, original } = issue;
              if (!suggestedText || target_block_indices.length === 0) continue;

              const primaryIdx = target_block_indices[0];

              if (modifiedIndices.has(primaryIdx)) {
                console.warn(`[Tempo2] Block ${primaryIdx} already modified, skipping duplicate patch for ${rule}`);
                continue;
              }

              if (rule === 'DLG-03') {
                // DLG-03 Special: The suggestion contains multi-block output
                // Format: "CHARACTER\n(parenthetical)\nVocalization.\n\nAction text"
                // We need to parse the suggestion back into typed blocks
                const suggParts = suggestedText.split('\n\n');
                const dialoguePart = suggParts[0] || '';
                const actionPart = suggParts.slice(1).join('\n\n').trim();

                // Parse the dialogue part into character + parenthetical + speech
                const dialogueLines = dialoguePart.split('\n');
                const charName = dialogueLines[0]?.trim() || '';
                const restLines = dialogueLines.slice(1);

                // Determine insertion point: replace the affected blocks with new structured blocks
                const minIdx = Math.min(...target_block_indices);
                const maxIdx = Math.max(...target_block_indices);
                const deleteCount = maxIdx - minIdx + 1;

                const replacementBlocks: ScriptBlock[] = [];

                // Character block
                if (charName) {
                  replacementBlocks.push({
                    id: `block-${Date.now()}-char-${minIdx}`,
                    type: 'character',
                    content: charName,
                    isAiSuggestion: true
                  });
                }

                // Parenthetical + Speech
                for (const line of restLines) {
                  const trimmed = line.trim();
                  if (!trimmed) continue;
                  if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
                    replacementBlocks.push({
                      id: `block-${Date.now()}-paren-${minIdx}`,
                      type: 'parenthetical',
                      content: trimmed,
                      isAiSuggestion: true
                    });
                  } else {
                    replacementBlocks.push({
                      id: `block-${Date.now()}-dlg-${minIdx}`,
                      type: 'dialogue',
                      content: trimmed,
                      isAiSuggestion: true
                    });
                  }
                }

                // Empty separator before action
                if (actionPart) {
                  replacementBlocks.push({
                    id: `block-${Date.now()}-sep-${minIdx}`,
                    type: 'action',
                    content: '',
                    isAiSuggestion: true
                  });
                  replacementBlocks.push({
                    id: `block-${Date.now()}-act-${minIdx}`,
                    type: 'action',
                    content: actionPart,
                    isAiSuggestion: true
                  });
                }

                // Splice: remove old blocks, insert new structured blocks
                newBlocks.splice(minIdx, deleteCount, ...replacementBlocks);

                // Mark all affected indices
                for (const idx of target_block_indices) modifiedIndices.add(idx);

              } else {
                // Standard O(1) mutation: direct content swap
                if (primaryIdx >= 0 && primaryIdx < newBlocks.length) {
                  newBlocks[primaryIdx] = { ...newBlocks[primaryIdx], content: suggestedText, isAiSuggestion: true };
                  modifiedIndices.add(primaryIdx);
                }
              }
            }

            const changeLabel = appState.config.language === 'pt'
              ? `Correções aplicadas (${issues.length} ${issues.length === 1 ? 'item' : 'itens'})`
              : `Corrections applied (${issues.length} ${issues.length === 1 ? 'item' : 'items'})`;

            // Enable line pagination for Tempo 2 fixes
            setAppState(prev => ({
              ...prev,
              config: { ...prev.config, linePagination: true }
            }));

            handleAddScriptVersion(newBlocks, changeLabel);
            return;
          }
        } catch (parseErr) {
          console.warn('[Tempo2] Failed to parse APPLY_AUDIT_FIXES for direct mutation, falling back to AI path.', parseErr);
        }
      }

      // ====================================================================
      // LEGACY PATH: AI-based rewrite (for non-review suggestions or
      // issues without target_block_indices)
      // ====================================================================

      // Check if it's a special camera direction application
      try {
        const parsedSuggestion = JSON.parse(suggestion);
        if (parsedSuggestion.type === 'CAMERA_DIRECTIONS') {
          const cameraData: SceneCameraDirection[] = parsedSuggestion.data;
          const updatedBlocks = [...activeVersion.blocks];
          const finalBlocks: ScriptBlock[] = [];

          for (let i = 0; i < updatedBlocks.length; i++) {
            const block = updatedBlocks[i];
            finalBlocks.push(block);

            if (block.type === 'scene-heading') {
              const sceneSlug = block.content.trim().toUpperCase();
              const sceneMatch = cameraData.find(d => d.slug.trim().toUpperCase() === sceneSlug);

              if (sceneMatch && sceneMatch.suggestions.length > 0) {
                const sug = sceneMatch.suggestions[0];
                const cueLine = sug.cueText.toUpperCase().endsWith('.') ? sug.cueText.toUpperCase() : `${sug.cueText.toUpperCase()}.`;

                finalBlocks.push({
                  id: `camera - dir - ${Date.now()} -${Math.random()} `,
                  type: 'camera-direction',
                  content: `${cueLine} \n${sceneMatch.intention} `
                });
              }
            }
          }

          handleAddScriptVersion(finalBlocks, "Direções de câmera aplicadas (formato exclusivo).");
          return;
        }

        if (parsedSuggestion.type === 'EFFECTS_SUGGESTIONS') {
          const effectsData: SceneEffectsResult[] = parsedSuggestion.data;
          const updatedBlocks = [...activeVersion.blocks];
          const finalBlocks: ScriptBlock[] = [];

          for (let i = 0; i < updatedBlocks.length; i++) {
            const block = updatedBlocks[i];
            finalBlocks.push(block);

            if (block.type === 'scene-heading') {
              const sceneSlug = block.content.trim().toUpperCase();
              const sceneMatch = effectsData.find(d =>
                d.sceneHeading.trim().toUpperCase() === sceneSlug ||
                (typeof d.sceneId === 'string' && d.sceneId.includes(sceneSlug))
              );

              if (sceneMatch && sceneMatch.suggestions.length > 0) {
                sceneMatch.suggestions.forEach(sug => {
                  finalBlocks.push({
                    id: `sfx - ${Date.now()} -${Math.random()} `,
                    type: 'sfx',
                    content: `(${sug.type}) ${sug.name.toUpperCase()} (${sug.why})`
                  });
                });
              }
            }
          }

          handleAddScriptVersion(finalBlocks, "Efeitos sonoros e visuais aplicados.");
          return;
        }
      } catch (e) {
        // Not a special JSON suggestion, proceed with standard AI rewrite
      }

      let newScriptText = await updateScriptWithSuggestion(currentScript, suggestion, appState.config, taskType || appState.activeAIModal || undefined);

      // Post-processing to remove unwanted AI artifacts
      const artifactsToRemove = [
        "Revised Script:",
        "**Revised Script**",
        "## Revised Script",
        "Here is the revised script:",
        "Updated Script:",
        "REVISED SCRIPT:"
      ];

      for (const artifact of artifactsToRemove) {
        if (newScriptText.includes(artifact)) {
          newScriptText = newScriptText.split(artifact).pop()?.trim() || newScriptText;
        }
      }

      // Remove markdown code blocks if present
      newScriptText = newScriptText.replace(/^```(fountain|markdown)?/i, '').replace(/```$/, '').trim();
      const { metadata, blocks } = splitMetadataAndParse(newScriptText);

      const authorCredit = appState.config.language === 'pt' ? 'Escrito por' : 'Written by';
      const unknownAuthor = appState.config.language === 'pt' ? 'Autor' : 'Author';
      const titleBlocks = createTitlePageBlocks(activeProject.name, activeProject.author || unknownAuthor, authorCredit, activeProject.basedOn, activeProject.contactInfo);
      const finalBlocks = [...titleBlocks, ...blocks];

      handleAddScriptVersion(finalBlocks, metadata);
    } catch (e) {
      console.error("Failed to apply suggestion", e);
      alert(t.error_generate_version);
      throw e;
    } finally {
      isGeneratingRef.current = false;
      setIsGeneratingVersion(false);
    }
  };

  const setActiveTab = (tab: TabOption) => {
    setAppState(prev => ({ ...prev, activeTab: tab }));
  };

  const setActiveAIModal = (modal: string | null) => {
    setAppState(prev => ({ ...prev, activeAIModal: modal }));
  };

  const setActiveAnalysisTask = (task: AnalysisResult | null) => {
    setAppState(prev => {
      let updatedProjects = prev.projects;
      // Persist scriptaudit results to the current version
      if (task?.type === 'scriptaudit' && prev.activeProjectId !== 'start-menu') {
        updatedProjects = prev.projects.map(p => {
          if (p.id === prev.activeProjectId) {
            return {
              ...p,
              scriptVersions: (p.scriptVersions || []).map(v =>
                v.id === p.activeVersionId ? { ...v, auditResult: task } : v
              )
            };
          }
          return p;
        });
      }

      return {
        ...prev,
        activeAnalysisTask: task,
        projects: updatedProjects
      };
    });
  };

  const toggleConfig = (key: keyof AppState['config']) => {
    setAppState(prev => ({
      ...prev,
      config: { ...prev.config, [key]: !prev.config[key] }
    }));
  };

  const setLanguage = (lang: 'en' | 'pt' | 'es' | 'fr') => {
    setAppState(prev => {
      // Also migrate all project version names to the new language
      const migratedProjects = prev.projects.map(p => ({
        ...p,
        scriptVersions: p.scriptVersions?.map(v => ({
          ...v,
          name: translateVersionName(v.name, lang)
        }))
      }));

      return {
        ...prev,
        projects: migratedProjects,
        config: { ...prev.config, language: lang }
      };
    });
  };

  const handleUpdateConfig = (updates: Partial<AppState['config']>) => {
    setAppState(prev => {
      let migratedProjects = prev.projects;
      if (updates.language) {
        migratedProjects = prev.projects.map(p => ({
          ...p,
          scriptVersions: p.scriptVersions?.map(v => ({
            ...v,
            name: translateVersionName(v.name, updates.language!)
          }))
        }));
      }
      return {
        ...prev,
        projects: migratedProjects,
        config: { ...prev.config, ...updates }
      };
    });
  };

  const toggleScriptState = () => {
    handleUpdateActiveScript([]);
  };

  const handleLogout = () => {
    // 1. Force Auto-Save
    if (user) {
      try {
        const stateToSave = {
          projects: appState.projects,
          activeProjectId: appState.activeProjectId,
          config: appState.config
        };
        const pruned = pruneStateForStorage(stateToSave);
        localStorage.setItem(getStorageKey(user.uid), JSON.stringify(pruned));
      } catch (e) {
        console.error("[Logout] Failed to save state to localStorage during logout.", e);
      }
    }

    // 2. Sign Out
    signOut();
  };



  return (
    <div className="flex h-screen w-screen bg-background text-text-primary font-sans overflow-hidden relative">

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-300">
          <div className="bg-[#1E1E1E] border border-border shadow-2xl rounded-2xl w-full max-w-md overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/5 flex items-center justify-between bg-[#202020]">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <PenTool size={18} className="text-blue-500" /> {isEditingTitlePage ? t.edit_title_page : t.start_script_modal_title}
              </h3>
              <button onClick={() => setShowNewProjectModal(false)} className="text-text-secondary hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-text-secondary uppercase mb-1 block flex items-center gap-2"><FileText size={12} /> {t.project_title_label}</label>
                <input
                  type="text"
                  value={newProjectTitle}
                  onChange={(e) => setNewProjectTitle(e.target.value)}
                  className="w-full bg-black/30 border border-border rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder={t.untitled}
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-bold text-text-secondary uppercase mb-1 block flex items-center gap-2"><User size={12} /> {t.author_label}</label>
                <input
                  type="text"
                  value={newProjectAuthor}
                  onChange={(e) => setNewProjectAuthor(e.target.value)}
                  className="w-full bg-black/30 border border-border rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Name..."
                />
              </div>
              <div>
                <label className="text-xs font-bold text-text-secondary uppercase mb-1 block flex items-center gap-2"><Link size={12} /> {t.based_on_label}</label>
                <input
                  type="text"
                  value={newProjectBasedOn}
                  onChange={(e) => setNewProjectBasedOn(e.target.value)}
                  className="w-full bg-black/30 border border-border rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Optional..."
                />
              </div>
              <div>
                <label className="text-xs font-bold text-text-secondary uppercase mb-1 block flex items-center gap-2"><Mail size={12} /> {t.contact_info_label}</label>
                <input
                  type="text"
                  value={newProjectContactInfo}
                  onChange={(e) => setNewProjectContactInfo(e.target.value)}
                  className="w-full bg-black/30 border border-border rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Optional..."
                />
              </div>
            </div>
            <div className="p-4 bg-[#202020] border-t border-white/5 flex justify-end gap-2">
              <button onClick={() => setShowNewProjectModal(false)} className="px-4 py-2 text-sm text-text-secondary hover:text-white transition-colors">
                {t.cancel}
              </button>
              <button onClick={confirmProjectSetup} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg shadow-lg shadow-blue-900/20 transition-all text-sm">
                {isEditingTitlePage ? t.save : t.create_project_btn}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trial Limit Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-300">
          <div className="bg-[#121212] border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] rounded-2xl w-full max-w-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
            <div className="p-4 flex justify-end border-b border-white/5 bg-[#161616]">
              <button
                onClick={() => setShowLimitModal(false)}
                className="text-gray-500 hover:text-white transition-all hover:rotate-90 duration-200"
              >
                <X size={22} />
              </button>
            </div>

            <div className="p-16 py-24 flex flex-col items-center text-center">
              <p className="text-2xl md:text-3xl text-white leading-tight font-medium max-w-lg">
                {t.trial_limit_reached_title}.
              </p>
              <p className="text-lg md:text-xl text-gray-400 mt-4 font-normal max-w-md">
                {t.trial_limit_reached_body}
              </p>
            </div>

            <div className="p-5 px-8 border-t border-white/5 flex justify-end bg-[#161616]">
              <button
                onClick={() => setShowLimitModal(false)}
                className="px-8 py-2.5 bg-[#3B82F6] hover:bg-blue-500 text-white font-bold rounded-lg transition-all text-lg shadow-lg shadow-blue-500/20 active:scale-95"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`shrink-0 h-full border-r border-border bg-surface flex flex-col relative transition-all duration-300 ease-in-out ${isLeftPanelOpen ? 'w-64' : 'w-12'}`}>
        <button onClick={() => setIsLeftPanelOpen(!isLeftPanelOpen)} className="absolute -right-3 top-20 z-50 w-6 h-6 bg-[#1E1E1E] border border-[#333] rounded-full flex items-center justify-center text-gray-400 hover:text-white shadow-md transition-colors hover:scale-110">
          {isLeftPanelOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className={`h-full w-64 flex flex-col transition-opacity duration-200 ${isLeftPanelOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <Suspense fallback={<LoadingFallback />}>
            <SidebarLeft
              analysis={appState.analysis}
              projects={appState.projects}
              activeProjectId={appState.activeProjectId}
              config={appState.config}
              saveStatus={saveStatus}
              onSwitchProject={handleSwitchProject}
              onCreateProject={() => handleCreateProject()}
              onRenameProject={handleRenameProject}
              onDeleteProject={handleDeleteProject}
              onUpdateProject={handleUpdateProject}
              onChangeLanguage={setLanguage}
              onNavigateToBlock={setNavBlockId}
              onUpdateBlock={handleUpdateBlock}
              onLogout={handleLogout}
            />
          </Suspense>
        </div>
        {!isLeftPanelOpen && (
          <div className="absolute inset-0 flex flex-col items-center pt-28 opacity-50 pointer-events-none">
            <div className="w-1 h-1 bg-gray-500 rounded-full mb-1"></div>
            <div className="w-1 h-1 bg-gray-500 rounded-full mb-1"></div>
            <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 h-full bg-background relative flex flex-col">
        <Suspense fallback={<LoadingFallback />}>
          <MainStage
            key={appState.activeProjectId}
            hasScript={hasScript}
            project={activeProject || { id: 'start-menu', name: 'Start Menu', blocks: [], scriptVersions: [], lastModified: 0 }}
            scriptBlocks={scriptBlocks}
            initialChatHistory={activeProject?.chatHistory || []}
            onUpload={handleUpdateActiveScript}
            onAddVersion={handleAddScriptVersion}
            onSwitchVersion={handleSwitchScriptVersion}
            onRenameVersion={handleRenameScriptVersion}
            onDeleteVersion={handleDeleteScriptVersion}
            onChatUpdate={handleChatUpdate}
            onUpdateChatTouched={handleUpdateChatTouched}
            onUpdateMetadata={handleUpdateVersionMetadata}
            onUpdateDetectedFormat={handleUpdateDetectedFormat}
            config={appState.config}
            onCreateProject={handleCreateProject}
            onConfigureProject={handleConfigureProject}
            onRenameProject={handleRenameProject}
            onUpdateProject={handleUpdateProject}
            activeFormatId={appState.activeFormatId}
            onUpdateFormatId={(id) => setAppState(prev => ({ ...prev, activeFormatId: id }))}
            activeBriefingAnswers={appState.activeBriefingAnswers}
            onUpdateBriefingAnswers={(answers) => setAppState(prev => ({ ...prev, activeBriefingAnswers: answers }))}
            initialStartMode={projectStartMode}
            onResetStartMode={() => setProjectStartMode(null)}
            navBlockId={navBlockId}
            onNavComplete={() => setNavBlockId(null)}
            // Pass Undo/Redo handlers
            onUndo={handleUndo}
            onRedo={handleRedo}
            onRecategorizeElement={handleRecategorizeElement}
            onCategorizeElements={handleCategorizeElements}
            analysis={appState.analysis}
            onUpdateConfig={handleUpdateConfig}
            onRequestCharacterEdit={(name) => setCharacterEditRequest({ name, timestamp: Date.now() })}
            onCheckConsistency={handleCheckConsistency}
            onEditTitlePage={handleEditTitlePage}
            availableFormats={availableFormats}
          />
        </Suspense>

        {/* Global Generator Loader */}
        {isGeneratingVersion && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
            <img
              src="/loading.svg"
              className="w-16 h-16 animate-spin object-contain opacity-90 drop-shadow-2xl"
              alt="Generating..."
            />
          </div>
        )}

        {appState.activeProjectId !== 'start-menu' && (
          <div className="absolute bottom-4 left-4 z-50">
            <button onClick={toggleScriptState} className="px-3 py-1 bg-surface border border-border text-xs text-text-secondary rounded hover:text-white transition-colors opacity-50 hover:opacity-100">
              [Dev: Clear Editor]
            </button>
          </div>
        )}
      </div>
      <div className={`shrink-0 h-full border-l border-border bg-surface flex flex-col relative transition-all duration-300 ease-in-out ${isRightPanelOpen ? 'w-80' : 'w-12'}`}>
        <button onClick={() => setIsRightPanelOpen(!isRightPanelOpen)} className="absolute -left-3 top-20 z-50 w-6 h-6 bg-[#1E1E1E] border border-[#333] rounded-full flex items-center justify-center text-gray-400 hover:text-white shadow-md transition-colors hover:scale-110">
          {isRightPanelOpen ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
        <div className={`h-full w-80 flex flex-col transition-opacity duration-200 ${isRightPanelOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <Suspense fallback={<LoadingFallback />}>
            <SidebarRight
              activeTab={appState.activeTab}
              onTabChange={setActiveTab}
              config={appState.config}
              onToggleConfig={toggleConfig}
              analysis={appState.analysis}
              scriptBlocks={scriptBlocks}
              onApplySuggestion={handleApplySuggestion}
              project={activeProject || { id: 'start-menu', name: 'Start Menu', blocks: [], scriptVersions: [], lastModified: 0 }}
              onUpdateScript={handleUpdateActiveScript}
              onUpdateCharacterProfile={handleUpdateCharacterProfile}
              onUpdateCharacterProfilesBulk={handleUpdateCharacterProfilesBulk}
              onResetCharactersModified={handleResetCharactersModified}
              onUpdateLocationProfile={handleUpdateLocationProfile}

              onUpdatePropProfile={handleUpdatePropProfile}
              onUpdateSfxProfile={handleUpdateSfxProfile}
              onUpdateCameraProfile={handleUpdateCameraProfile}
              onUpdateTransitionProfile={handleUpdateTransitionProfile}
              onNavigateToBlock={setNavBlockId}
              onAddVersion={handleAddScriptVersion}
              onAddCustomMethod={handleAddCustomMethod}
              onToggleCustomMethod={handleToggleCustomMethod}
              onDeleteCustomMethod={handleDeleteCustomMethod}
              onRecategorizeElement={handleRecategorizeElement}
              activeAIModal={appState.activeAIModal}
              activeAnalysisTask={appState.activeAnalysisTask}
              onSetActiveAIModal={setActiveAIModal}
              onSetActiveAnalysisTask={setActiveAnalysisTask}
              externalCharacterEditRequest={characterEditRequest}
              onUpdateVerifiedSegments={handleUpdateVerifiedSegments}
              onInjectPlotFix={handleInjectPlotFix}
              onGenerateFixOptions={handleGenerateFixOptions}
            />
          </Suspense>
        </div>
        {!isRightPanelOpen && (
          <div className="absolute inset-0 flex flex-col items-center pt-28 opacity-50 pointer-events-none">
            <div className="w-1 h-1 bg-gray-500 rounded-full mb-1"></div>
            <div className="w-1 h-1 bg-gray-500 rounded-full mb-1"></div>
            <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
          </div>
        )}
      </div>
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}

export default function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (window.location.pathname === '/privacy-policy') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <PrivacyPolicy />
      </Suspense>
    );
  }

  if (window.location.pathname === '/login') {
    if (user) {
      window.location.href = '/';
      return null;
    }
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Auth />
      </Suspense>
    );
  }

  if (window.location.pathname === '/docs') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <DocsPage />
      </Suspense>
    );
  }

  if (!user) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <LandingPage />
      </Suspense>
    );
  }

  return <AuteurDraft />;
}
